import {
  USER_STORAGE_PREFIX,
  userStorageKey,
} from "@medoma/shared-employee/userStorageKey";
import * as Sentry from "@sentry/react";

// Keys written before browser storage was scoped per employee.
//
// This is the first of two steps. Values are copied, not moved, so reverting
// the release that introduced scoping puts every employee back where they were.
// Nothing reads a legacy key any more, so the leftovers are inert. The
// follow-up release deletes them and this module with them. It should also delete
// the scoped "theme" key, since the theme is now read from "colorTheme".
//
// The two exceptions are deleted here and now, because leaving them is the
// thing we are fixing: the AI support chat text, and the toggle that unmasks
// personal identity numbers.

type ILegacyKey = { legacy: string; key: string };

// Written once the copy has run, so a preference the employee clears later is
// not restored the next time they sign in.
const MIGRATED_MARKER_KEY = "legacyStorageMigrated";

// Keys that already embedded the employee's id, so the value is unambiguously
// theirs and only the name changes.
const RENAMED_USER_KEYS: ILegacyKey[] = [
  {
    legacy: "activities-view-expanded-row-layout",
    key: "activities.expandedRowLayout",
  },
  { legacy: "planning-view-mode", key: "planning.viewMode" },
];

// Shared keys whose value says nothing about who set it or what they saw.
const ADOPTED_KEYS: ILegacyKey[] = [
  { legacy: "theme", key: "theme" },
  { legacy: "activeSidePanel", key: "sidePanel.active" },
  { legacy: "planningViewExpanded", key: "planning.viewExpanded" },
  { legacy: "planningViewMapShown", key: "planning.mapShown" },
  { legacy: "planningViewMapPosition", key: "planning.mapPosition" },
  { legacy: "planningview-sidebar-grouping", key: "planning.sidebarGrouping" },
  { legacy: "patientActivitiesViewMode", key: "patient.activitiesViewMode" },
];

// Panel sizes are one key per panel and layout, so they are matched by prefix
// and keep their name — only the scoping changes.
const ADOPTED_KEY_PREFIXES = [
  "split-pane-leading-width-",
  "planning-to-plan-and-shifts-and-map-resizable-panel-",
  "planning-shifts-and-map-resizable-panel-",
  "planning-details-and-map-resizable-panel-",
];

// Filters, carried over only where this browser has served one employee.
// Hiding rows is asymmetric: dropping a filter shows everything, which is the
// safe direction, while inheriting one shows less with nothing on screen
// saying so.
const SINGLE_EMPLOYEE_ONLY_KEYS: ILegacyKey[] = [
  { legacy: "timeFilter", key: "planning.timeFilter" },
  { legacy: "showOnlyAssignedToMe", key: "activities.showOnlyAssignedToMe" },
  {
    legacy: "planning-selected-competences",
    key: "planning-selected-competences",
  },
  { legacy: "planning-selected-teams", key: "planning-selected-teams" },
  { legacy: "templates.competenceFilter", key: "templates.competenceFilter" },
  {
    legacy: "templatesWithoutPatient.competenceFilter",
    key: "templatesWithoutPatient.competenceFilter",
  },
];

// Unmasking a personal identity number is never inherited: after the upgrade
// everyone starts masked and turns it back on themselves.
const DELETED_KEYS = ["reportingShowFullPersonalIdentityNumber"];

// The chat text itself, so whether it is carried over follows the filters —
// but the shared copy goes either way.
const DELETED_SESSION_KEY: ILegacyKey = {
  legacy: "ai-support-chat-messages",
  key: "aiSupport.chatMessages",
};

/**
 * Whether every trace of use in this browser belongs to the employee signing
 * in. Center writes `selectedSites` for each employee before they reach any
 * page, so an employee who has used this browser has left a key here. No keys
 * at all means there is nothing to go on, which counts as unknown.
 */
const isSingleEmployeeBrowser = (userId: string) => {
  const ownPrefix = `${USER_STORAGE_PREFIX}${userId}.`;
  const userKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith(USER_STORAGE_PREFIX),
  );
  return (
    userKeys.length > 0 && userKeys.every((key) => key.startsWith(ownPrefix))
  );
};

const copyOver = ({
  storage,
  legacy,
  key,
  userId,
}: {
  storage: Storage;
  legacy: string;
  key: string;
  userId: string;
}) => {
  const value = storage.getItem(legacy);
  if (value === null) {
    return;
  }

  const scopedKey = userStorageKey({ userId, key });
  // Anything set since the upgrade is the employee's current choice and wins.
  if (storage.getItem(scopedKey) === null) {
    storage.setItem(scopedKey, value);
  }
};

let migratedUserId: string | undefined;

/**
 * Retires pre-scoping storage for the signed-in employee. Runs during render
 * rather than in an effect: it has to happen before any hook reads its key, and
 * repeating it is a no-op, so there is nothing to synchronize or clean up.
 */
export const migrateLegacyStorage = (userId: string) => {
  if (migratedUserId === userId) {
    return;
  }
  migratedUserId = userId;

  try {
    const markerKey = userStorageKey({ userId, key: MIGRATED_MARKER_KEY });
    if (localStorage.getItem(markerKey) !== null) {
      return;
    }

    // Read before anything is written, so keys copied below cannot be mistaken
    // for a second employee's traces.
    const singleEmployee = isSingleEmployeeBrowser(userId);

    const copy = (storage: Storage, { legacy, key }: ILegacyKey) =>
      copyOver({ storage, legacy, key, userId });

    for (const renamed of RENAMED_USER_KEYS) {
      copy(localStorage, {
        legacy: `${renamed.legacy}-${userId}`,
        key: renamed.key,
      });
    }

    for (const adopted of ADOPTED_KEYS) {
      copy(localStorage, adopted);
    }

    for (const legacy of Object.keys(localStorage)) {
      if (ADOPTED_KEY_PREFIXES.some((prefix) => legacy.startsWith(prefix))) {
        copy(localStorage, { legacy, key: legacy });
      }
    }

    if (singleEmployee) {
      for (const filter of SINGLE_EMPLOYEE_ONLY_KEYS) {
        copy(localStorage, filter);
      }
      copy(sessionStorage, DELETED_SESSION_KEY);
    }

    for (const legacy of DELETED_KEYS) {
      localStorage.removeItem(legacy);
    }
    sessionStorage.removeItem(DELETED_SESSION_KEY.legacy);

    localStorage.setItem(markerKey, new Date().toISOString());
  } catch (error) {
    // SecurityError when site data is blocked. The app works without storage.
    Sentry.captureException(error);
  }
};

export const _resetForTesting = () => {
  migratedUserId = undefined;
};
